import React, { useEffect, useMemo, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, ReceiptText, ShieldCheck, Landmark, AlertTriangle } from "lucide-react";

const localMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const localDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const money = (value) => formatIDR(Number(value || 0));

export default function VatOutputTax() {
  const [period, setPeriod] = useState(localMonth());
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    tax_period: localMonth(),
    amount: "",
    payment_date: localDate(),
    ntpn: "",
    billing_code: "",
    note: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/tax/vat-output/summary", { params: { period } }),
        api.get("/tax/vat-output/payments", { params: { period } }),
      ]);
      setSummary(s.data || null);
      setPayments(p.data || []);
      setForm((current) => ({ ...current, tax_period: period }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Data PPN gagal dimuat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const ntpnValid = useMemo(
    () => /^[A-Za-z0-9]{16}$/.test(String(form.ntpn || "")),
    [form.ntpn],
  );

  const submit = async (event) => {
    event.preventDefault();
    if (!ntpnValid) {
      toast.error("NTPN wajib tepat 16 karakter alfanumerik. Huruf bersifat case-sensitive.");
      return;
    }
    const amount = Number(form.amount || 0);
    if (!(amount > 0)) {
      toast.error("Nominal pembayaran harus lebih besar dari 0");
      return;
    }
    setSaving(true);
    try {
      await api.post("/tax/vat-output/payments", {
        ...form,
        amount,
      });
      toast.success("Pembayaran PPN berhasil dicatat dan jurnal dibuat dengan referensi NTPN");
      setForm((current) => ({
        ...current,
        amount: "",
        ntpn: "",
        billing_code: "",
        note: "",
      }));
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Pembayaran PPN gagal dicatat");
    } finally {
      setSaving(false);
    }
  };

  const cards = [
    ["PPN Keluaran", summary?.output_tax_credit, "Credit 2102 dari penjualan"],
    ["Pembalikan PPN", summary?.output_tax_reversal, "Debit 2102 dari refund PAID"],
    ["PPN Keluaran Neto", summary?.net_output_tax, "Keluaran dikurangi pembalikan"],
    ["Sudah Disetor", summary?.payments_for_tax_period, "Pembayaran dengan NTPN"],
    ["Sisa PPN Keluaran", summary?.remaining_output_tax_for_period, "Belum diselesaikan di ledger"],
  ];

  return (
    <div className="space-y-6" data-testid="vat-output-tax-page">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-600">
            <Landmark className="h-4 w-4" /> Pajak & Akuntansi
          </div>
          <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-950">PPN Keluaran & NTPN</h1>
          <p className="mt-1 text-sm text-slate-500">Ledger kewajiban PPN keluaran mitra dan bukti pembayaran pajak.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
        <div className="flex gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <strong>Batas perhitungan v1:</strong> KRAYA menghitung PPN Keluaran dari penjualan/refund.
            PPN Masukan yang dapat dikreditkan belum dicatat, sehingga angka ini <strong>bukan otomatis PPN Kurang Bayar SPT Masa PPN</strong>.
            Cocokkan nilai setor dengan Coretax/SPT sebelum mencatat pembayaran.
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        {cards.map(([label, value, note]) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
            <div className="mt-2 text-xl font-black text-slate-950">{money(value)}</div>
            <div className="mt-2 text-xs leading-5 text-slate-500">{note}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[380px_1fr]">
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            <h2 className="font-black text-slate-950">Catat Pembayaran PPN</h2>
          </div>
          <p className="mt-2 text-xs leading-5 text-slate-500">
            Saat disimpan: Dr PPN Keluaran (2102), Cr Bank / QRIS (1102). NTPN menjadi referensi jurnal.
          </p>

          <div className="mt-5 space-y-4">
            <label className="block text-sm font-semibold text-slate-700">
              Masa Pajak
              <input
                type="month"
                required
                value={form.tax_period}
                onChange={(e) => setForm({ ...form, tax_period: e.target.value })}
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 font-normal"
              />
            </label>

            <label className="block text-sm font-semibold text-slate-700">
              Nominal Dibayar
              <input
                type="number"
                min="0.01"
                step="0.01"
                required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 font-normal"
                placeholder="0"
              />
            </label>

            <label className="block text-sm font-semibold text-slate-700">
              Tanggal Pembayaran
              <input
                type="date"
                required
                value={form.payment_date}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 font-normal"
              />
            </label>

            <label className="block text-sm font-semibold text-slate-700">
              NTPN <span className="text-rose-600">*</span>
              <input
                data-testid="vat-ntpn-input"
                maxLength={16}
                required
                value={form.ntpn}
                onChange={(e) => setForm({ ...form, ntpn: e.target.value.replace(/\s/g, "") })}
                className={`mt-1.5 w-full rounded-xl border px-3 py-2.5 font-mono font-normal ${
                  form.ntpn && !ntpnValid ? "border-rose-300 bg-rose-50" : "border-slate-200"
                }`}
                placeholder="16 karakter sesuai BPN"
              />
              <span className="mt-1 block text-xs font-normal text-slate-500">
                Harus 16 karakter alfanumerik dan case-sensitive.
              </span>
            </label>

            <label className="block text-sm font-semibold text-slate-700">
              Kode Billing
              <input
                value={form.billing_code}
                onChange={(e) => setForm({ ...form, billing_code: e.target.value })}
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 font-normal"
                placeholder="Opsional"
              />
            </label>

            <label className="block text-sm font-semibold text-slate-700">
              Catatan
              <textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="mt-1.5 min-h-20 w-full rounded-xl border border-slate-200 px-3 py-2.5 font-normal"
                placeholder="Opsional"
              />
            </label>

            <button
              type="submit"
              disabled={saving || !ntpnValid}
              className="w-full rounded-xl bg-slate-950 px-4 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "Menyimpan..." : "Catat Pembayaran + Jurnal"}
            </button>
          </div>
        </form>

        <div className="rounded-2xl border border-slate-200 bg-white">
          <div className="flex items-center gap-2 border-b border-slate-100 p-5">
            <ReceiptText className="h-5 w-5 text-blue-600" />
            <div>
              <h2 className="font-black text-slate-950">Riwayat Pembayaran PPN</h2>
              <p className="text-xs text-slate-500">Setiap record PAID wajib memiliki NTPN.</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Tanggal</th>
                  <th className="px-4 py-3">Masa</th>
                  <th className="px-4 py-3">NTPN</th>
                  <th className="px-4 py-3">Kode Billing</th>
                  <th className="px-4 py-3 text-right">Nominal</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Belum ada pembayaran untuk masa ini.</td></tr>
                ) : payments.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">{row.payment_date || "-"}</td>
                    <td className="px-4 py-3">{row.tax_period || "-"}</td>
                    <td className="px-4 py-3 font-mono font-semibold">{row.ntpn || "-"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{row.billing_code || "-"}</td>
                    <td className="px-4 py-3 text-right font-bold">{money(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

